import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, Users, User, Mail, Phone,
  Heart, Rocket, Trophy, Sparkles, Building,
  Upload, Send, Check, AlertCircle, Calendar, DollarSign,
  ChevronDown, FileText, Globe, Linkedin, Github
} from 'lucide-react';
import '../styles/Careers.css';

const Careers = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    location: '',
    experience: '',
    currentRole: '',
    currentCompany: '',
    linkedin: '',
    portfolio: '',
    skills: '',
    salary: '',
    startDate: '',
    referral: '',
    coverLetter: ''
  });

  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [selectedPosition, setSelectedPosition] = useState('');

  const values = [
    {
      icon: '🚀',
      title: 'Innovation First',
      description: 'Push boundaries with cutting-edge AI and technology'
    },
    {
      icon: '🤝',
      title: 'Collaborative Spirit',
      description: 'Work with brilliant minds in a supportive environment'
    },
    {
      icon: '🌱',
      title: 'Growth Mindset',
      description: 'Continuous learning and professional development'
    },
  ];


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({
          ...prev,
          resume: 'File size must be less than 5MB'
        }));
        return;
      }
      setResume(file);
      setErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const handlePositionSelect = (position) => {
    setSelectedPosition(position);
    setFormData(prev => ({
      ...prev,
      position: position
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.position.trim()) newErrors.position = 'Please select a position';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const formDataToSend = new FormData();

    // Add all form fields
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key] || '');
    });

    // Add resume if exists
    if (resume) {
      formDataToSend.append('resume', resume);
    }

    try {
      const response = await fetch('http://localhost:8000/api/careers', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Your application has been submitted successfully! We\'ll be in touch soon.'
        });

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          position: '',
          location: '',
          experience: '',
          currentRole: '',
          currentCompany: '',
          linkedin: '',
          portfolio: '',
          skills: '',
          salary: '',
          startDate: '',
          referral: '',
          coverLetter: ''
        });
        setResume(null);
        setSelectedPosition('');

        // Reset file input
        const fileInput = document.getElementById('resume');
        if (fileInput) fileInput.value = '';
      } else {
        throw new Error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit your application. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="careers-page">

      {/* Hero Section */}
      <section className="careers-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="careers-hero-content"
          >
            <h1 className="careers-hero-title">Join Our Mission</h1>
            <p className="careers-hero-subtitle">
              Shape the future of AI consulting and help organizations navigate their transformation journey
            </p>
          </motion.div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="culture-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="culture-content"
          >
            <h2 className="section-title">Our Culture & Values</h2>
            <p className="culture-text">
              At Kosmoz Consulting, we believe in fostering an environment where innovation thrives,
              collaboration is celebrated, and every team member has the opportunity to make a
              meaningful impact on our clients and the industry.
            </p>

            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="value-card"
                >
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <section className="application-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="application-content"
          >
            <h2 className="section-title">Apply Now</h2>
            <p className="section-subtitle">Ready to join us? Fill out the form below and we'll be in touch</p>

            {submitStatus.message && (
              <div className={`alert alert-${submitStatus.type}`}>
                {submitStatus.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
                {submitStatus.message}
              </div>
            )}

            <form className="application-form" onSubmit={handleSubmit}>

              {/* Personal Information */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <User size={20} /> Personal Information
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className={errors.fullName ? 'error' : ''}
                    />
                    {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="location">Current Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="San Francisco, CA"
                    />
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <Briefcase size={20} /> Professional Information
                </h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="position">Position Applying For *</label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className={errors.position ? 'error' : ''}
                    >
                      <option value="">Select a position</option>
                      <option value="Data Analyst">Data Analyst</option>
                      <option value="UI/UX Designer">UI/UX Designer</option>
                      <option value="ML Engineer">ML Engineer</option>
                      <option value="Gen AI Engineer">Gen AI Engineer</option>
                      <option value="Business Analyst">Business Analyst</option>
                      <option value="Full Stack Developer">Full Stack Developer</option>
                    </select>
                    {errors.position && <span className="error-message">{errors.position}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="experience">Years of Experience</label>
                    <select
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                    >
                      <option value="">Select experience</option>
                      <option value="0-1 years">0-1 years</option>
                      <option value="2-3 years">2-3 years</option>
                      <option value="4-5 years">4-5 years</option>
                      <option value="6-8 years">6-8 years</option>
                      <option value="9+ years">9+ years</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="currentRole">Current Role</label>
                    <input
                      type="text"
                      id="currentRole"
                      name="currentRole"
                      value={formData.currentRole}
                      onChange={handleChange}
                      placeholder="Senior Software Engineer"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="currentCompany">Current Company</label>
                    <input
                      type="text"
                      id="currentCompany"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleChange}
                      placeholder="Tech Corp"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="linkedin">LinkedIn Profile</label>
                    <input
                      type="url"
                      id="linkedin"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/johndoe"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="portfolio">Portfolio/Website</label>
                    <input
                      type="url"
                      id="portfolio"
                      name="portfolio"
                      value={formData.portfolio}
                      onChange={handleChange}
                      placeholder="https://johndoe.com"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="skills">Key Skills</label>
                    <textarea
                      id="skills"
                      name="skills"
                      value={formData.skills}
                      onChange={handleChange}
                      rows="3"
                      placeholder="List your key skills separated by commas (e.g., Python, Machine Learning, React, AWS)"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FileText size={20} /> Additional Information
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="salary">Salary Expectations</label>
                    <input
                      type="text"
                      id="salary"
                      name="salary"
                      value={formData.salary}
                      onChange={handleChange}
                      placeholder="$150,000 - $180,000"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="startDate">Available Start Date</label>
                    <input
                      type="text"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      placeholder="e.g., Immediately, 2 weeks notice"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="referral">How did you hear about us?</label>
                    <input
                      type="text"
                      id="referral"
                      name="referral"
                      value={formData.referral}
                      onChange={handleChange}
                      placeholder="e.g., LinkedIn, Employee referral, Job board"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="coverLetter">Cover Letter / Additional Notes</label>
                    <textarea
                      id="coverLetter"
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleChange}
                      rows="6"
                      placeholder="Tell us why you're interested in joining Kosmoz Consulting and what makes you a great fit for this role..."
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="resume">Upload Resume</label>
                    <div className="file-upload">
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                      />
                      <label htmlFor="resume" className="file-upload-label">
                        <Upload size={20} />
                        {resume ? resume.name : 'Choose file (PDF, DOC, DOCX - Max 5MB)'}
                      </label>
                    </div>
                    {errors.resume && <span className="error-message">{errors.resume}</span>}
                  </div>
                </div>
              </div>

              <div className="form-footer">
                <p className="form-note">* Required fields</p>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      Submit Application
                      <Send size={18} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;